import { useEffect, useMemo, useRef } from 'react'
import type { ReactNode } from 'react'
import { Animated, Platform, View, type ViewProps } from 'react-native'
import { landingSectionRevealStyles as styles } from './LandingSectionReveal.styles'

interface LandingSectionRevealProps extends ViewProps {
  children: ReactNode
  delayMs?: number
  threshold?: number
  /** Force-reveal after this many ms regardless of scroll position (phone fallback). */
  maxDelayMs?: number
}

export function LandingSectionReveal({
  children,
  delayMs = 0,
  threshold = 1.05,
  maxDelayMs = 1400,
  ...viewProps
}: LandingSectionRevealProps) {
  const opacityAnim = useRef(new Animated.Value(0)).current
  const translateYAnim = useRef(new Animated.Value(28)).current
  const hasAnimatedRef = useRef(false)
  const revealId = useMemo(
    () => `landing-reveal-${Math.random().toString(36).slice(2, 10)}`,
    [],
  )

  useEffect(() => {
    const startReveal = () => {
      if (hasAnimatedRef.current) return
      hasAnimatedRef.current = true
      Animated.parallel([
        Animated.timing(opacityAnim, {
          toValue: 1,
          duration: 460,
          delay: delayMs,
          useNativeDriver: Platform.OS !== 'web',
        }),
        Animated.timing(translateYAnim, {
          toValue: 0,
          duration: 520,
          delay: delayMs,
          useNativeDriver: true,
        }),
      ]).start()
    }

    if (Platform.OS !== 'web') {
      startReveal()
      return undefined
    }

    const evaluateInView = () => {
      if (hasAnimatedRef.current || typeof document === 'undefined') return
      const node = document.getElementById(revealId)
      if (!node) return
      const rect = node.getBoundingClientRect()
      const viewportHeight = window.innerHeight || 0
      if (rect.top <= viewportHeight * threshold) {
        startReveal()
      }
    }

    evaluateInView()
    // Re-check after layout settles — mobile browsers may not have final
    // positions when the first synchronous call runs
    const layoutSettle = setTimeout(evaluateInView, 120)
    // Hard fallback: reveal regardless of scroll after maxDelayMs (catches
    // mobile browsers where scroll events fire inconsistently)
    const hardFallback = setTimeout(startReveal, maxDelayMs)
    window.addEventListener('scroll', evaluateInView, { passive: true })
    window.addEventListener('resize', evaluateInView)

    return () => {
      clearTimeout(layoutSettle)
      clearTimeout(hardFallback)
      window.removeEventListener('scroll', evaluateInView)
      window.removeEventListener('resize', evaluateInView)
    }
  }, [delayMs, maxDelayMs, opacityAnim, revealId, threshold, translateYAnim])

  return (
    <View
      nativeID={revealId}
      style={styles.container}
      {...viewProps}
    >
      <Animated.View
        style={[
          styles.animatedContent,
          {
            opacity: opacityAnim,
            transform: [{ translateY: translateYAnim }],
          },
        ]}
      >
        {children}
      </Animated.View>
    </View>
  )
}
